/**
 * ManoPaieskos Component (Employer Saved Searches)
 * 
 * Allows employers to save and manage candidate search criteria.
 * Similar to SavedSearches.tsx but for employer candidate searches.
 * 
 * @route /mano-paieskos
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Settings, Trash2, Search as SearchIcon, ChevronRight, Plus } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import CandidateSearchFilters from '../components/Skelbimai/CandidateSearchFilters';
import { FilterOptions, filtersToQueryParams } from './Skelbimai';
import './ManoPaieskos.scss';

interface EmployerSavedSearch {
  id: number;
  title: string;
  filters_json?: string;
  // Search keywords
  keywords?: string[];
  // Personal Data
  location?: string[];
  age_range?: string;
  gender?: string;
  // Work Experience
  experience_years?: string;
  previous_companies?: string;
  positions?: string[];
  // Education
  education_level?: string[];
  field_of_study?: string[];
  // Skills
  technical_skills?: string[];
  languages?: string[];
  // Requirements
  desired_salary?: string;
  desired_work_type?: string[];
  desired_location?: string[];
  
  is_subscribed: boolean;
  total_results: number;
  new_results: number;
  created_at: string;
  updated_at: string;
}

const EMPTY_FILTERS: FilterOptions = {
  searchKeyword: '',
  keywords: [],
  location: [],
  experience: [],
  education: [],
  languages: [],
  computerSkills: [],
  drivingLicense: [],
  salary: '',
  workType: [],
  age: '',
  gender: [],
  status: [],
  activity: '',
  cvLanguage: [],
  hasPhoto: false,
  hasRecommendation: false,
  companyNames: [],
  lastWorkField: [],
  companyActivity: [],
};

const savedSearchToFilterOptions = (search: EmployerSavedSearch): FilterOptions => {
  if (search.filters_json) {
    try { return JSON.parse(search.filters_json); } catch (_) {}
  }
  // Legacy fallback — best-effort mapping from old flat fields
  return {
    ...EMPTY_FILTERS,
    keywords: search.keywords || search.positions || [],
    location: search.location || [],
    age: search.age_range || '',
    gender: search.gender ? [search.gender] : [],
    education: search.education_level || [],
    languages: search.languages || [],
    computerSkills: search.technical_skills || [],
    salary: search.desired_salary || '',
    workType: search.desired_work_type || [],
    companyNames: search.previous_companies ? [search.previous_companies] : [],
  };
};

const ManoPaieskos: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [savedSearches, setSavedSearches] = useState<EmployerSavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [allCVs, setAllCVs] = useState<any[]>([]);
  const [loadingCVs, setLoadingCVs] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSearch, setEditingSearch] = useState<EmployerSavedSearch | null>(null);
  const [selectedSearches, setSelectedSearches] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');
  // Modal state — title + full filter panel state
  const [modalTitle, setModalTitle] = useState('');
  const [modalFilters, setModalFilters] = useState<FilterOptions>(EMPTY_FILTERS);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') {
      fetchSavedSearches();
      fetchAllCVs();
    }
  }, [isAuthenticated, user]);


  const fetchSavedSearches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-saved-searches/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSavedSearches(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCVs = async () => {
    try {
      setLoadingCVs(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/cv/employer/visible`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setAllCVs(result.data || result || []);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    } finally {
      setLoadingCVs(false);
    }
  };

  const countMatchingCVs = (search: EmployerSavedSearch): number => {
    return allCVs.filter(cv => {
      // Location filter
      if (search.location && search.location.length > 0) {
        const cvLocation = cv.location?.toLowerCase() || '';
        const matchesLocation = search.location.some(city => 
          city && cvLocation.includes(city.toLowerCase())
        );
        if (!matchesLocation) return false;
      }

      // Add more filters as needed
      return true;
    }).length;
  };

  const countNewCVs = (search: EmployerSavedSearch): number => {
    const matchingCVs = allCVs.filter(cv => {
      // Location filter
      if (search.location && search.location.length > 0) {
        const cvLocation = cv.location?.toLowerCase() || '';
        const matchesLocation = search.location.some(city => 
          city && cvLocation.includes(city.toLowerCase())
        );
        if (!matchesLocation) return false;
      }
      return true;
    });

    // Count CVs created in last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return matchingCVs.filter(cv => {
      if (cv.created_at) {
        const cvCreatedDate = new Date(cv.created_at);
        return cvCreatedDate >= sevenDaysAgo;
      }
      return false;
    }).length;
  };

  const handleCreateSearch = () => {
    setEditingSearch(null);
    setModalTitle('');
    setModalFilters(EMPTY_FILTERS);
    setShowCreateModal(true);
  };

  const handleEditSearch = (search: EmployerSavedSearch) => {
    setEditingSearch(search);
    setModalTitle(search.title);
    setModalFilters(savedSearchToFilterOptions(search));
    setShowCreateModal(true);
  };

  const handleSaveSearch = async () => {
    if (!modalTitle.trim()) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const f = modalFilters;
      const payload = {
        title: modalTitle.trim(),
        filters_json: JSON.stringify(f),
        // Legacy fields for display / backward compat
        keywords: f.keywords.length > 0 ? f.keywords : null,
        location: f.location.length > 0 ? f.location : null,
        age_range: f.age || null,
        gender: f.gender.length > 0 ? f.gender[0] : null,
        experience_years: f.experience.length > 0 ? f.experience.join(', ') : null,
        positions: f.keywords.length > 0 ? f.keywords : null,
        education_level: f.education.length > 0 ? f.education : null,
        technical_skills: f.computerSkills.length > 0 ? f.computerSkills : null,
        languages: f.languages.length > 0 ? f.languages : null,
        desired_salary: f.salary || null,
        desired_work_type: f.workType.length > 0 ? f.workType : null,
        is_subscribed: editingSearch ? editingSearch.is_subscribed : true,
      };

      const url = editingSearch
        ? `${API_URL}/employer-saved-searches/${editingSearch.id}`
        : `${API_URL}/employer-saved-searches`;

      const response = await fetch(url, {
        method: editingSearch ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowCreateModal(false);
        fetchSavedSearches();
        showToast(editingSearch ? 'Paieška atnaujinta' : 'Paieška išsaugota', 'success');
      } else {
        showToast('Klaida išsaugant paiešką', 'error');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      showToast('Klaida išsaugant paiešką', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSearch = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-saved-searches/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSavedSearches();
      }
    } catch (error) {
      console.error('Error deleting search:', error);
    }
  };

  const handleGoToSearch = (search: EmployerSavedSearch) => {
    const filters = savedSearchToFilterOptions(search);
    const params = filtersToQueryParams(filters);
    params.set('mode', 'employer');
    navigate(`/skelbimai?${params.toString()}`);
  };

  const handleModalFiltersChange = useCallback((f: FilterOptions) => {
    setModalFilters(f);
  }, []);

  const formatCriteria = (search: EmployerSavedSearch): string => {
    const criteria: string[] = [];

    if (search.keywords && search.keywords.length > 0) {
      criteria.push(`Raktažodžiai: ${search.keywords.join(', ')}`);
    }
    if (search.positions && search.positions.length > 0) {
      criteria.push(`Pareigos: ${search.positions.join(', ')}`);
    }
    if (search.gender) {
      criteria.push(search.gender === 'male' ? 'Vyras' : 'Moteris');
    }
    if (search.age_range) {
      criteria.push(`${search.age_range} metų`);
    }
    if (search.experience_years) {
      criteria.push(`Patirtis: ${search.experience_years}`);
    }
    if (search.location && search.location.length > 0) {
      criteria.push(search.location.join(', '));
    }

    return criteria.length > 0 ? criteria.join(', ') : 'Visi kriterijai';
  };

  const handleToggleSelectAll = () => {
    const filteredSearches = filterSearchesByTitle(savedSearches);
    
    if (selectedSearches.length === filteredSearches.length && filteredSearches.length > 0) {
      setSelectedSearches([]);
    } else {
      setSelectedSearches(filteredSearches.map(s => s.id));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedSearches(prev => 
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedSearches.length === 0) return;

    if (!await confirm(`Ar tikrai norite ištrinti ${selectedSearches.length} paieškų? Šio veiksmo negalima atšaukti.`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      await Promise.all(
        selectedSearches.map(id =>
          fetch(`${API_URL}/employer-saved-searches/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        )
      );

      setSelectedSearches([]);
      fetchSavedSearches();
    } catch (error) {
      console.error('Error deleting searches:', error);
      showToast('Įvyko klaida trinant paieškas', 'error');
    }
  };

  const filterSearchesByTitle = (searches: EmployerSavedSearch[]) => {
    if (!searchTitleFilter.trim()) return searches;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return searches.filter(search => 
      search.title.toLowerCase().includes(filterLower)
    );
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="mano-paieskos-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an employer
  if (!isAuthenticated || user?.user_type !== 'employer') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mano-paieskos-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <Link to="/">Titulinis</Link>
            <ChevronRight size={14} />
            <span>Mano paieškos</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedSearches.length > 0 && selectedSearches.length === filterSearchesByTitle(savedSearches).length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedSearches.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Paieškos pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <SearchIcon size={16} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={handleCreateSearch}>
              <Plus size={16} />
              <span>Nauja paieška</span>
            </button>
          </div>
        </div>

        <div className="saved-section">
          {loading || loadingCVs ? (
            <div className="loading">Kraunama...</div>
          ) : savedSearches.length === 0 ? (
            <div className="empty-state">
              <SearchIcon size={48} />
              <p>Dar neturite išsaugotų paieškų</p>
              <button className="btn-primary" onClick={handleCreateSearch}>
                Sukurti pirmą paiešką
              </button>
            </div>
          ) : (
            <div className="searches-grid">
                  {filterSearchesByTitle(savedSearches).map((search) => (
                    <div key={search.id} className={`search-card ${selectedSearches.includes(search.id) ? 'selected' : ''}`}>
                      <div className="search-card-header">
                        <div className="checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={selectedSearches.includes(search.id)}
                            onChange={() => handleToggleSelect(search.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="header-actions">
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSearch(search);
                            }}
                            title="Nustatymai"
                          >
                            <Settings size={18} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSearch(search.id);
                            }}
                            title="Ištrinti"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="search-card-body" onClick={() => handleGoToSearch(search)}>
                        <h3 className="search-title">{search.title}</h3>
                        <p className="search-criteria">
                          <span className="criteria-label">Kriterijai:</span><br />
                          {formatCriteria(search)}
                        </p>
                      </div>

                      <div className="search-card-footer">
                        <span className="result-count">
                          Iš viso: <strong>{countMatchingCVs(search)}</strong>
                        </span>
                        <span className="new-count">
                          Naujų: <span className="badge-new">{countNewCVs(search)}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal — full filter panel */}
      {showCreateModal && (
        <div className="modal-overlay search-edit-overlay">
          <div className="modal-content search-edit-modal">
            <div className="modal-header">
              <h2>{editingSearch ? 'Redaguoti paiešką' : 'Nauja paieška'}</h2>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>

            <div className="modal-body search-edit-body">
              {/* Search title */}
              <div className="search-edit-title-row">
                <label htmlFor="modalSearchTitle">Paieškos pavadinimas *</label>
                <input
                  id="modalSearchTitle"
                  type="text"
                  className="search-edit-title-input"
                  value={modalTitle}
                  onChange={(e) => setModalTitle(e.target.value)}
                  placeholder="pvz., Eksporto vadovas"
                />
              </div>

              {/* Full filter panel — same component as on the Skelbimai page */}
              <CandidateSearchFilters
                isVisible={true}
                currentFilters={modalFilters}
                onFiltersChange={handleModalFiltersChange}
                hideSaveButton={true}
              />
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Atšaukti
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveSearch}
                disabled={!modalTitle.trim() || isSaving}
              >
                {isSaving ? 'Saugoma...' : editingSearch ? 'Atnaujinti' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManoPaieskos;

